// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useState } from 'react';
import axios from 'axios';
import { Button, useToast } from '@loa/ui-components';
import { TiptapEditor } from '../../../invitation/TiptapEditor';
import { proposalApi } from '../../../../api/proposalApi';
import type { IntelligenceUseCaseCode } from '../../../../api/intelligenceApi';
import type { ProposalSection, RichTextSectionContent } from '../../../../types/proposal';
import { useAiBlockDraft } from '../useAiBlockDraft';
import { AiGenerateRow } from './AiGenerateRow';

export type SectionRichTextProps = Readonly<{
  proposalId: string;
  /**
   * Anchors the AI drafting session to the relationship, when known. Only
   * used when `aiSectionLabel` is set.
   */
  profileId?: string;
  section: ProposalSection;
  /** Only true for optional sections (APPENDICES) — shows the Remove button. */
  removable?: boolean;
  onSaved: (updated: ProposalSection) => void;
  onRemoved?: (sectionKey: string) => void;
  /**
   * Enables the real "Generate with AI" affordance above the editor. The
   * value is the section's name as it appears in the drafting use case's
   * system prompt (e.g. "our-understanding-of-you" for the Breakthrough
   * Report, or "Sustainability Factors" for the SoS builders) — woven into
   * the steering message so the assistant drafts the right block. Undefined
   * hides the AI row (e.g. DECLARATIONS legal boilerplate).
   */
  aiSectionLabel?: string;
  /**
   * The intelligence use case the AI drafting session runs under. Defaults to
   * `SOS_BUILDER` (the pension/investment/protection Statement-of-Suitability
   * sections); the Breakthrough Report's authored sections pass
   * `REPORT_BUILDER`. Only used when `aiSectionLabel` is set.
   */
  aiUseCase?: IntelligenceUseCaseCode;
  /**
   * Renders the "Client view" preview (BR-P5, PRD §5 / RQ-BR8): the same
   * Tiptap-rendered HTML is shown, disabled and without its toolbar, and the
   * AI-generate row, Save button and Remove button are all hidden. Defaults
   * to false (advisor editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function firstBlockHtml(section: ProposalSection): string {
  const content = section.content as RichTextSectionContent | null;
  return content?.blocks?.[0]?.html ?? '';
}

/** Escape the plain-language text the model returns for narrative/bullets. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Normalise the REPORT_BUILDER reply into an HTML fragment. The prompt
 * describes several of the authored sections as narrative or bullets (PRD §11
 * / V62 seed), so the assistant legitimately replies with `{narrative}` or
 * `{bullets}` as well as `{html}`. All render as the section's single HTML
 * block, so convert the first shape that's present rather than rejecting a
 * valid-but-differently-shaped reply. Returns null when no usable text is
 * found (surfaces as the "try again or edit manually" parse error).
 */
function replyToHtml(parsed: Record<string, unknown>): string | null {
  if (typeof parsed.html === 'string' && parsed.html.trim().length > 0) {
    return parsed.html;
  }
  const parts: string[] = [];
  if (typeof parsed.narrative === 'string' && parsed.narrative.trim().length > 0) {
    parts.push(`<p>${escapeHtml(parsed.narrative.trim())}</p>`);
  }
  if (Array.isArray(parsed.bullets)) {
    const items = parsed.bullets
      .filter((b): b is string => typeof b === 'string' && b.trim().length > 0)
      .map((b) => `<li>${escapeHtml(b.trim())}</li>`);
    if (items.length > 0) {
      parts.push(`<ul>${items.join('')}</ul>`);
    }
  }
  return parts.length > 0 ? parts.join('') : null;
}

/**
 * Rich-text section renderer shared by SUSTAINABILITY, PRECAUTIONS,
 * NEXT_STEPS, DECLARATIONS, the optional APPENDICES section (PRD §3.2 /
 * §3.3), and the Breakthrough Report's authored sections (BR_UNDERSTANDING,
 * BR_GOALS, BR_GIVING_LEGACY, BR_MARKET_INSIGHTS, BR_NEXT_STEPS — PRD §4 /
 * §11) — all share `{blocks: [{title?, html}]}`. Single-block in practice
 * this phase; Save replaces `content.blocks[0].html` wholesale, mirroring
 * the wizard v1 SoS builder's Tiptap-per-section pattern. When
 * `aiSectionLabel` is set, a "Generate with AI" row drafts the block via the
 * REPORT_BUILDER intelligence use case (BR-P6) and applies it through the
 * generic `html` block on `applyAiBlock`.
 */
export function SectionRichText({
  proposalId,
  profileId,
  section,
  removable = false,
  onSaved,
  onRemoved,
  aiSectionLabel,
  aiUseCase = 'SOS_BUILDER',
  readOnly = false,
}: SectionRichTextProps) {
  const { showToast } = useToast();
  const [html, setHtml] = useState(() => firstBlockHtml(section));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Reset the editor state when the builder switches to a different section
  // that also renders through this component (rich-text → rich-text). Without
  // this the local `html` state persists and the previous section's text is
  // shown. Standard React "reset state on prop change during render" pattern.
  const [trackedKey, setTrackedKey] = useState(section.sectionKey);
  if (trackedKey !== section.sectionKey) {
    setTrackedKey(section.sectionKey);
    setHtml(firstBlockHtml(section));
    setDirty(false);
  }

  const handleChange = useCallback((next: string) => {
    setHtml(next);
    setDirty(true);
  }, []);

  const handleAiApplied = useCallback(
    (updated: ProposalSection) => {
      setHtml(firstBlockHtml(updated));
      setDirty(false);
      onSaved(updated);
      showToast('AI draft applied', 'success');
    },
    [onSaved, showToast],
  );

  const ai = useAiBlockDraft({
    proposalId,
    profileId,
    sectionKey: section.sectionKey,
    block: 'html',
    useCase: aiUseCase,
    buildMessage: (instruction) =>
      `Draft the ${aiSectionLabel ?? section.title} section for proposal ${proposalId} as a ` +
      'single client-facing HTML block. Reply with ONLY a JSON object {"html": "..."} using ' +
      `<p>, <ul>, <li>, <strong>, <br/>.${instruction ? ` Instruction: ${instruction}` : ''}`,
    buildPayload: (parsed) => {
      const html = replyToHtml(parsed);
      if (html === null) {
        throw new Error('no usable block content');
      }
      return { html };
    },
    onApplied: handleAiApplied,
  });

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const existingBlock = (section.content as RichTextSectionContent | null)?.blocks?.[0];
      const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
        content: { blocks: [{ title: existingBlock?.title, html }] },
      });
      setDirty(false);
      onSaved(updated);
      showToast(`${section.title} saved`, 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, `Failed to save ${section.title}`), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, section, proposalId, html, onSaved, showToast]);

  const handleRemove = useCallback(async () => {
    if (removing) return;
    setRemoving(true);
    try {
      await proposalApi.removeSection(proposalId, section.sectionKey);
      onRemoved?.(section.sectionKey);
      showToast(`${section.title} removed`, 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, `Failed to remove ${section.title}`), 'error');
    } finally {
      setRemoving(false);
    }
  }, [removing, proposalId, section, onRemoved, showToast]);

  return (
    <div className="pension-builder__section" data-testid={`section-rich-text-${section.sectionKey}`}>
      {!readOnly && aiSectionLabel && (
        <AiGenerateRow
          idSuffix={section.sectionKey.toLowerCase()}
          instruction={ai.instruction}
          onInstructionChange={ai.setInstruction}
          onGenerate={() => void ai.generate()}
          generating={ai.generating}
          error={ai.error}
        />
      )}
      <TiptapEditor
        key={section.sectionKey}
        content={html}
        onChange={handleChange}
        disabled={readOnly}
        hideToolbar={readOnly}
        minHeight="280px"
        maxHeight="520px"
        ariaLabel={`${section.title} content`}
      />
      {!readOnly && (
        <div className="pension-builder__section-footer">
          {removable && (
            <Button
              variant="outlined"
              colour="red"
              onClick={handleRemove}
              disabled={saving || removing}
              loading={removing}
              data-testid={`section-rich-text-remove-btn-${section.sectionKey}`}
            >
              Remove section
            </Button>
          )}
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={saving || removing || !dirty}
            loading={saving}
            data-testid={`section-rich-text-save-btn-${section.sectionKey}`}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

export default SectionRichText;
